/** Annual Returns: cashflow In/Out vs granular category P&L class. */

export type PlClass =
  | 'income'
  | 'expense'
  | 'internal_transfer'
  | 'financing'
  | 'capital'
  | 'tax'
  | 'reversal'
  | 'unset';

export type AnnualPlBucket =
  | 'income'
  | 'expenses'
  | 'internal_in'
  | 'internal_out'
  | 'financing_in'
  | 'financing_out'
  | 'capital_in'
  | 'capital_out'
  | 'tax_in'
  | 'tax_out'
  | 'reversal_in'
  | 'reversal_out'
  | 'needs';

export type CashflowSide = 'in' | 'out' | 'other';

const HEADING_NAMES = new Set(['in', 'out', 'n/a', 'na', 'income', 'expense']);

export const normalizePlClass = (raw: string | null | undefined): PlClass => {
  const s = String(raw || '').trim().toLowerCase().replace(/[-\s]/g, '_');
  if (
    s === 'income' ||
    s === 'expense' ||
    s === 'internal_transfer' ||
    s === 'financing' ||
    s === 'capital' ||
    s === 'tax' ||
    s === 'reversal'
  ) {
    return s;
  }
  if (s === 'capex' || s === 'property_purchase' || s === 'property_purchases') return 'capital';
  if (s === 'refund' || s === 'refunds' || s === 'correction' || s === 'wash') return 'reversal';
  return 'unset';
};

export const isCashflowHeadingName = (name: string): boolean =>
  HEADING_NAMES.has(String(name || '').trim().toLowerCase());

export const plClassForCategoryName = (
  categoryName: string | null | undefined,
  classByName: Map<string, PlClass>
): PlClass => {
  const name = String(categoryName || '').trim();
  if (!name || name === '—' || name === '-') return 'unset';
  if (isCashflowHeadingName(name)) return 'unset';
  const keyed = classByName.get(name.toLowerCase());
  if (keyed !== undefined) return keyed;
  return 'unset';
};

const UNCLASSIFIED_NAMES = new Set(['unclassified', 'unclassified - needs review']);

export type ReviewRowPlClassSource = {
  pl_class?: string | null;
  category?: string | null;
  tags?: Array<{
    category_id?: number | null;
    category_name?: string | null;
    category?: { id?: number | null; name?: string | null } | null;
    pl_class?: string | null;
  }> | null;
};

const tagCategoryName = (tag: NonNullable<ReviewRowPlClassSource['tags']>[number]): string =>
  String(tag.category_name || tag.category?.name || '').trim();

/** Granular category for P&L (skip cash-flow headings such as Out/In). */
export const granularDisplayCategoryName = (row: ReviewRowPlClassSource): string => {
  const display = String(row.category || '').trim();
  if (display && display !== '—' && display !== '-' && !isCashflowHeadingName(display)) {
    return display;
  }
  for (const tag of row.tags || []) {
    const name = tagCategoryName(tag);
    if (!name || isCashflowHeadingName(name) || UNCLASSIFIED_NAMES.has(name.toLowerCase())) continue;
    return name;
  }
  return display === '—' || display === '-' ? '' : display;
};

/** P&L class from the TX's own category (review-rows), then the viewer's catalog. */
export const plClassFromReviewRow = (
  row: ReviewRowPlClassSource,
  classByName: Map<string, PlClass>
): PlClass => {
  const fromRow = normalizePlClass(row.pl_class);
  if (fromRow !== 'unset') return fromRow;
  for (const tag of row.tags || []) {
    const name = tagCategoryName(tag);
    if (!name || isCashflowHeadingName(name)) continue;
    const fromTag = normalizePlClass(tag.pl_class);
    if (fromTag !== 'unset') return fromTag;
  }
  return plClassForCategoryName(granularDisplayCategoryName(row), classByName);
};

/** Revolut memos like “Refund from Google Cloud” / “Refund from Apple.com”. */
export const descriptionLooksLikeRefund = (
  description?: string | null,
  reference?: string | null
): boolean => {
  const blob = `${description || ''} ${reference || ''}`.toLowerCase();
  return /\brefund\b/.test(blob);
};

export const annualPlBucket = (
  cashflow: CashflowSide,
  plClass: PlClass,
  opts?: { description?: string | null; reference?: string | null }
): AnnualPlBucket | null => {
  if (cashflow !== 'in' && cashflow !== 'out') return null;
  if (plClass === 'internal_transfer') {
    return cashflow === 'in' ? 'internal_in' : 'internal_out';
  }
  if (plClass === 'financing') {
    return cashflow === 'in' ? 'financing_in' : 'financing_out';
  }
  if (plClass === 'capital') {
    return cashflow === 'in' ? 'capital_in' : 'capital_out';
  }
  if (plClass === 'tax') {
    return cashflow === 'in' ? 'tax_in' : 'tax_out';
  }
  if (plClass === 'reversal') {
    return cashflow === 'in' ? 'reversal_in' : 'reversal_out';
  }
  if (plClass === 'income' && cashflow === 'in') return 'income';
  if (plClass === 'expense' && cashflow === 'out') return 'expenses';
  if (
    plClass === 'expense' &&
    cashflow === 'in' &&
    descriptionLooksLikeRefund(opts?.description, opts?.reference)
  ) {
    return 'expenses';
  }
  return 'needs';
};

export const ANNUAL_SECTION_TITLES: Record<AnnualPlBucket, string> = {
  income: 'Income',
  expenses: 'Expenses',
  internal_in: 'Internal transfers · In',
  internal_out: 'Internal transfers · Out',
  financing_in: 'Financing · In',
  financing_out: 'Financing · Out',
  capital_in: 'Property purchases · In',
  capital_out: 'Property purchases · Out',
  tax_in: 'Tax · In',
  tax_out: 'Tax · Out',
  reversal_in: 'Reversals · In',
  reversal_out: 'Reversals · Out',
  needs: 'Needs P&L class',
};

/** On-screen Annual Returns accordions: one section per type, In and Out together. */
export const ANNUAL_DISPLAY_SECTIONS = [
  { key: 'income', title: 'Income', buckets: ['income'] },
  { key: 'expenses', title: 'Expenses', buckets: ['expenses'] },
  { key: 'internal', title: 'Internal transfers', buckets: ['internal_in', 'internal_out'] },
  { key: 'financing', title: 'Financing', buckets: ['financing_in', 'financing_out'] },
  { key: 'capital', title: 'Property purchases', buckets: ['capital_in', 'capital_out'] },
  { key: 'tax', title: 'Tax', buckets: ['tax_in', 'tax_out'] },
  { key: 'reversal', title: 'Reversals', buckets: ['reversal_in', 'reversal_out'] },
  { key: 'needs', title: 'Needs P&L class', buckets: ['needs'] },
] as const;

export type AnnualDisplaySectionKey = (typeof ANNUAL_DISPLAY_SECTIONS)[number]['key'];

export const rowsForAnnualDisplaySection = <T,>(
  grouped: Record<AnnualPlBucket, T[]>,
  section: (typeof ANNUAL_DISPLAY_SECTIONS)[number]
): T[] => section.buckets.flatMap((bucket) => grouped[bucket] || []);

export const buildPlClassByName = (
  categories: Array<{ name?: string | null; pl_class?: string | null }>
): Map<string, PlClass> => {
  const map = new Map<string, PlClass>();
  categories.forEach((c) => {
    const name = String(c.name || '').trim().toLowerCase();
    if (!name || isCashflowHeadingName(name)) return;
    map.set(name, normalizePlClass(c.pl_class));
  });
  return map;
};
